import { useEffect, useState } from "react";
import { DollarSign, Package, ShoppingBag, Users, Clock, Boxes, TrendingUp, Warehouse } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend } from "recharts";
import { apiClient } from "@/lib/api";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    apiClient.get("/admin/analytics").then((res) => setStats(res.data)).catch(() => {});
  }, []);

  if (!stats) return <div className="h-64 bg-white rounded-md animate-pulse" data-testid="admin-dashboard-loading" />;

  const cards = [
    { label: "Total Revenue", value: `$${stats.total_revenue.toFixed(2)}`, icon: DollarSign },
    { label: "Total Orders", value: stats.total_orders, icon: ShoppingBag },
    { label: "Pending Orders", value: stats.pending_orders, icon: Clock },
    { label: "Products", value: stats.total_products, icon: Package },
    { label: "Customers", value: stats.total_customers, icon: Users },
  ];

  const profitCards = [
    { label: "Inventory Cost (On Hand)", value: `$${(stats.inventory_cost_value ?? 0).toFixed(2)}`, icon: Boxes },
    { label: "Inventory Retail Value", value: `$${(stats.inventory_retail_value ?? 0).toFixed(2)}`, icon: Warehouse },
    { label: "Potential Profit (On Hand)", value: `$${(stats.potential_profit ?? 0).toFixed(2)}`, icon: TrendingUp },
    { label: "Profit Earned (Paid Orders)", value: `$${(stats.realized_profit ?? 0).toFixed(2)}`, icon: DollarSign, highlight: true },
  ];

  return (
    <div data-testid="admin-dashboard-page">
      <h1 className="text-2xl font-display font-semibold text-slate-900 mb-6">Dashboard Overview</h1>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        {cards.map((c) => (
          <div key={c.label} className="bg-white border border-slate-200 rounded-md shadow-sm p-5" data-testid={`admin-stat-${c.label.toLowerCase().replace(/\s+/g, "-")}`}>
            <c.icon className="h-5 w-5 text-brandblue-600 mb-3" strokeWidth={1.5} />
            <p className="text-2xl font-bold text-slate-900">{c.value}</p>
            <p className="text-xs text-slate-500 mt-1">{c.label}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="admin-profit-cards">
        {profitCards.map((c) => (
          <div key={c.label} className={`bg-white border rounded-md shadow-sm p-5 ${c.highlight ? "border-emerald-200 bg-emerald-50/40" : "border-slate-200"}`} data-testid={`admin-stat-${c.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
            <c.icon className={`h-5 w-5 mb-3 ${c.highlight ? "text-emerald-600" : "text-brandblue-600"}`} strokeWidth={1.5} />
            <p className={`text-2xl font-bold ${c.highlight ? "text-emerald-700" : "text-slate-900"}`}>{c.value}</p>
            <p className="text-xs text-slate-500 mt-1">{c.label}</p>
          </div>
        ))}
        {stats.products_missing_cost > 0 && (
          <p className="col-span-2 lg:col-span-4 text-xs text-amber-600 -mt-2" data-testid="admin-missing-cost-note">
            {stats.products_missing_cost} product{stats.products_missing_cost > 1 ? "s" : ""} missing a unit cost — add costs in Products for accurate profit numbers.
          </p>
        )}
      </div>

      {stats.monthly_trend?.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-md shadow-sm p-6 mb-8" data-testid="admin-monthly-trend">
          <h2 className="text-sm font-semibold text-slate-900 mb-4">Monthly Revenue &amp; Profit (Paid Orders)</h2>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={stats.monthly_trend} margin={{ top: 4, right: 8, left: -12, bottom: 0 }}>
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#64748b" }} />
              <YAxis tick={{ fontSize: 11, fill: "#64748b" }} tickFormatter={(v) => `$${v}`} />
              <Tooltip formatter={(v) => `$${Number(v).toFixed(2)}`} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="revenue" name="Revenue" fill="#2563eb" radius={[4, 4, 0, 0]} />
              <Bar dataKey="profit" name="Profit" fill="#10b981" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-md shadow-sm p-6">
        <h2 className="text-sm font-semibold text-slate-900 mb-4">Top Selling Products (All Time)</h2>
        {stats.top_products.length === 0 ? (
          <p className="text-sm text-slate-400" data-testid="admin-no-top-products">No sales data yet.</p>
        ) : (
          <table className="w-full text-sm text-slate-600">
            <tbody>
              {stats.top_products.map((p) => (
                <tr key={p._id} className="border-b border-slate-100">
                  <td className="py-2">{p._id}</td>
                  <td className="py-2 text-right font-semibold text-slate-900">{p.quantity} units</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
